import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import Button from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import AddSubscribers from "@WhiteCap/Components/OSNSubscriptions/AddSubscribers";
import ManageSubscribers from "@WhiteCap/Components/OSNSubscriptions/ManageSubscribers";
import {
    subscriptionsModalStyles,
    SubscriptionsModalStyles,
} from "@WhiteCap/Components/OSNSubscriptions/SoaSubscriptionsModal";
import applyOcSubscriptions from "@WhiteCap/Handlers/SubscriptionsDashboard/OrderCompeteSubscriptions/ApplyOrderCompleteSubscriptions";
import loadOrderCompleteSubscribers from "@WhiteCap/Handlers/SubscriptionsDashboard/OrderCompeteSubscriptions/GetOrderCompleteSubscribers";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import {
    OcOrderStatusSummaryModel,
    SubscribedEmailDto,
} from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";
import isEqual from "lodash/isEqual";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    extendedStyles?: SubscriptionsModalStyles;
    isModalOpen: boolean;
    closeModalHandler: (value: boolean) => void;
    modalTitle: string;
    changeCtaBtnPosition: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    const currentSubscriptionCustomerId = state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId;
    const subscriptionsData =
        state.WhiteCap.subscriptionsDashboardData.osnType === "accountLevel"
            ? state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions
            : state.WhiteCap.subscriptionsDashboardData.jobSiteSubscriptionsById[currentSubscriptionCustomerId];
    const subscribersCount = subscriptionsData.ocOrderStatusSummary?.count;
    const emails = subscriptionsData.ocOrderStatusSummary?.subscribedEmails;

    return {
        subscribersCount,
        emails,
        subscriptionsData,
        notificationType: state.WhiteCap.subscriptionsDashboardData.osnType,
        currentSubscriptionCustomerId: state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId,
        isApplyingSubscriptionsChanges: state.WhiteCap.subscriptionsDashboardData.isApplyingSubscriptionsChanges,
    };
};

const mapDispatchToProps = {
    loadOrderCompleteSubscribers,
    applyOcSubscriptions,
};

const styles = subscriptionsModalStyles;

const ocSubscriptionsModal = ({
    isModalOpen,
    closeModalHandler,
    subscriptionsData,
    loadOrderCompleteSubscribers,
    currentSubscriptionCustomerId,
    emails,
    isApplyingSubscriptionsChanges,
    changeCtaBtnPosition,
    applyOcSubscriptions,
    modalTitle
}: Props) => {
    const applyButtonText = siteMessage("OSN_Subscriptions_Modal_Apply_Btn_Text");
    const cancelButtonText = siteMessage("OSN_Subscriptions_Modal_Cancel_Btn_Text");
    // State created to compare if something is changed by user in popup.
    const [dataFromAPI, setDataFromAPI] = useState<OcOrderStatusSummaryModel>();
    const subscribersCount = subscriptionsData?.ocOrderStatusSummary?.count;
    const dispatch = useDispatch();
    const toasterContext = useContext(ToasterContext);

    const hasPhoneNumberErrors = !!(
        emails && emails?.filter((emailObj: SubscribedEmailDto) => emailObj.isEditing).length > 0
    );

    const isApplyButtonDisabled = isApplyingSubscriptionsChanges || hasPhoneNumberErrors;

    const handleProceed = () => {
        // Update all data at once
        if (isApplyButtonDisabled) {
            return;
        }

        // Remove isEditing key from email objects
        const emailsToSend = emails?.map(({ isEditing, ...rest }) => rest) || [];

        if (isEqual(dataFromAPI, subscriptionsData.ocOrderStatusSummary)) {
            // Close modal on click of apply if nothing is changed. 
            closeModalHandler(false);
            return;
        }

        applyOcSubscriptions({
            customerId: currentSubscriptionCustomerId,
            subscribedEmails: emailsToSend || [],
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
            onSuccess: () => {
                closeModalHandler(false);
            },
        });
    };

    useEffect(() => {
        if (!currentSubscriptionCustomerId) {
            return;
        }
        loadOrderCompleteSubscribers({
            customerId: currentSubscriptionCustomerId,
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
            onSuccess(data) {
                setDataFromAPI(data);
            },
        });
    }, []);

    const handleAddSubscriber = (email: string, onSuccessCallback: () => void) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/OcSubscriptions/CompleteAddSubscriber",
            result: {
                email,
                phone: "",
                isSubscribedToReceiveSMS: false,
            },
        });
        onSuccessCallback();
    };

    const handleRemoveEmail = (emailId: string) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/OcSubscriptions/CompleteRemoveSubscriber",
            value: emailId,
        });
    };

    const handleUpdateSubscriberDetails = (
        email: string,
        phoneNo: string,
        isSubscribedToReceiveSMS: boolean,
        id?: string,
        isActive?: boolean,
        isEditing?: boolean,
    ) => {
        const subscribedEmailDto: SubscribedEmailDto = {
            id,
            email,
            phone: phoneNo,
            isSubscribedToReceiveSMS,
            isActive,
            isEditing,
        };
        dispatch({
            type: "Custom/SubscriptionDashboard/OcSubscriptions/CompleteUpdatingSubscriber",
            result: {
                customerId: currentSubscriptionCustomerId,
                subscribedEmailDto,
            },
        });
    };

    const handleRemovePhoneNumber = (emailId: string, onRemovePhoneSuccessCallback: () => void) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/OcSubscriptions/CompleteRemovePhoneNumber",
            result: {
                emailId,
            },
        });
        onRemovePhoneSuccessCallback();
    };

    return (
        <Modal
            {...styles.modal}
            headline={modalTitle}
            isOpen={isModalOpen}
            handleClose={() => closeModalHandler(false)}
        >
            <StyledWrapper {...styles.body}>
                <LoadingOverlay {...styles.loadingOverlay} loading={isApplyingSubscriptionsChanges}>
                    <AddSubscribers
                        addSubscriber={handleAddSubscriber}
                        emails={emails}
                        subscriptionModalType="ocOrderStatusSummary"
                    />
                    <ManageSubscribers
                        subscribersCount={subscribersCount}
                        emails={emails || []}
                        removeEmail={handleRemoveEmail}
                        updateSubscriberDetails={handleUpdateSubscriberDetails}
                        removePhoneNumber={handleRemovePhoneNumber}
                    />
                </LoadingOverlay>
            </StyledWrapper>
            <StyledWrapper {...styles.footer}>
                {changeCtaBtnPosition ? (
                    <>
                        <Button {...styles.proceedButton} onClick={handleProceed} disabled={isApplyButtonDisabled}>
                            {applyButtonText}
                        </Button>
                        <Button
                            {...styles.cancelButton}
                            onClick={() => closeModalHandler(false)}
                            data-test-selector="cancelButton"
                        >
                            {cancelButtonText}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            {...styles.cancelButton}
                            onClick={() => closeModalHandler(false)}
                            data-test-selector="cancelButton"
                        >
                            {cancelButtonText}
                        </Button>
                        <Button {...styles.proceedButton} onClick={handleProceed} disabled={isApplyButtonDisabled}>
                            {applyButtonText}
                        </Button>
                    </>
                )}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ocSubscriptionsModal);
